import React, { useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { cancelInstance } from "../../lib/schools";
import FormTextField from "../FormTextField";
import FormLabel from "../FormLabel";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import { FieldHint, FORM_STYLE_CARD_CLASS } from "../createEvent/CreateEventFormUi";
import DangerConfirmModal from "../DangerConfirmModal";

export default function ClassEditWeekTab({
  instanceId,
  sessionLabel,
  cancelled,
  onCancelled,
}) {
  const [substitute, setSubstitute] = useState("");
  const [savingSub, setSavingSub] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  async function saveSubstitute() {
    if (!substitute.trim()) {
      toast.error("Enter a teacher name.");
      return;
    }
    setSavingSub(true);
    try {
      toast.message("Substitute teacher isn’t saved yet", {
        description: "This field will persist when session notes ship.",
        duration: 4000,
      });
    } finally {
      setSavingSub(false);
    }
  }

  async function confirmCancelSession() {
    if (!instanceId) return;
    setCancelling(true);
    try {
      await cancelInstance(instanceId);
      toast.success("Session cancelled. Registered students will be notified.");
      setConfirmCancel(false);
      onCancelled?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="max-w-page mx-auto px-4 mt-6 space-y-4" data-testid="class-dash-edit-tab">
      <ReadableCard className={`${FORM_STYLE_CARD_CLASS} space-y-4`}>
        <div>
          <h2 className="font-ui font-semibold text-[17px] text-ink">Edit Week</h2>
          <FieldHint className="mt-1">
            Overrides for {sessionLabel || "this date"} only — not the whole series.
          </FieldHint>
        </div>

        {cancelled ? (
          <p className="font-ui text-[14px] text-ink/80 bg-cream border border-black/[0.08] rounded-card px-4 py-3">
            This session is already cancelled.
          </p>
        ) : (
          <>
            <div className="space-y-2">
              <FormTextField
                label="Substitute Teacher"
                value={substitute}
                onChange={(e) => setSubstitute(e.target.value)}
                placeholder="Teacher name"
                data-testid="class-dash-substitute-name"
              />
              <ReadableNavButton
                type="button"
                className="w-full sm:w-auto"
                disabled={savingSub || !substitute.trim()}
                onClick={saveSubstitute}
                data-testid="class-dash-substitute-save"
              >
                {savingSub ? "Saving…" : "Save Substitute"}
              </ReadableNavButton>
            </div>

            <div className="pt-4 border-t border-black/[0.08] space-y-2">
              <FormLabel className="block">Cancel Session</FormLabel>
              <FieldHint>
                Closes RSVPs and attendance for this date only and notifies registered students.
              </FieldHint>
              <ReadableNavButton
                type="button"
                variant="danger"
                className="w-full sm:w-auto"
                onClick={() => setConfirmCancel(true)}
                data-testid="class-dash-cancel-session"
              >
                Cancel Session
              </ReadableNavButton>
            </div>
          </>
        )}
      </ReadableCard>

      <DangerConfirmModal
        open={confirmCancel}
        title="Cancel this session?"
        message={`This closes RSVPs for ${sessionLabel || "this date"} only and notifies registered students. The weekly series continues.`}
        confirmLabel="Cancel session"
        onClose={() => setConfirmCancel(false)}
        onConfirm={confirmCancelSession}
        busy={cancelling}
        testId="class-dash-cancel-confirm"
      />
    </div>
  );
}
